import {UserAvatar} from '@gaia/components/Avatar';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import gettext from '@gaia/utils/django/gettext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Divider, MenuList, Popover, useMediaQuery} from '@mui/material';
import {
    forwardRef,
    type HTMLAttributes,
    type HTMLProps,
    type MouseEvent,
    type ReactNode,
    useCallback,
    useRef,
    useState,
} from 'react';

import {MenuItemAnchor} from './MenuItemAnchor';

interface ToolbarProps extends HTMLAttributes<HTMLButtonElement> {
    isFloating?: boolean;
}

function Toolbar({isFloating = false, style, children, ...props}: ToolbarProps) {
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const ref = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const onMenuClose = useCallback(() => setIsOpen(false), []);

    const onAnchorClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsOpen(true);
    }, []);

    const {id: userId, image, displayName, isRanger, isStaff, isAuthenticated} = useUser();

    if (!isAuthenticated) return null;

    return (
        <>
            <button
                {...props}
                ref={ref}
                onClick={onAnchorClick}
                style={{
                    ...style,
                    background: 'none',
                    border: 0,
                    paddingRight: 0,
                    paddingLeft: theme.spacing(1),
                }}
            >
                {children || (
                    <>
                        <UserAvatar
                            id={userId}
                            image={image}
                            name={displayName}
                            ranger={isRanger}
                            staff={isStaff}
                        />

                        {isMediumScreen || isFloating ? null : (
                            <span style={{paddingLeft: 6}}>{displayName}</span>
                        )}

                        <ExpandMoreIcon style={{fontSize: 18}} />
                    </>
                )}
            </button>
            <Popover
                anchorEl={ref.current}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                open={isOpen}
                onClose={onMenuClose}
            >
                <HamburgerMenu />
            </Popover>
        </>
    );
}

const dividerStyle = {margin: 0};

function AdminNavigation() {
    return (
        <>
            <Divider style={dividerStyle} />

            <MenuItemAnchor href='/report/paid_subs/'>Marketing Stats</MenuItemAnchor>
            <MenuItemAnchor href='/dashboard/'>GaiaCloud Stats</MenuItemAnchor>
            <MenuItemAnchor href='/admin/'>Datastore</MenuItemAnchor>
            <MenuItemAnchor href='/giftcodes/'>Generate Gift Code</MenuItemAnchor>
            <MenuItemAnchor href='/giftcodes/use/'>Apply Gift Code</MenuItemAnchor>
        </>
    );
}

interface HamburgerMenuProps extends HTMLProps<HTMLUListElement> {
    children?: ReactNode;
}

const HamburgerMenu = forwardRef<HTMLDivElement>(function HamburgerMenuInner(
    _props: HamburgerMenuProps,
    ref
) {
    const {isAuthenticated, isStaff, isTeamAdmin} = useUser();

    if (!isAuthenticated) return null;

    return (
        <MenuList aria-label='Account navigation' component='nav' ref={ref}>
            <MenuItemAnchor href='/datasummary/tracks/'>{gettext('Tracks')}</MenuItemAnchor>
            <MenuItemAnchor href='/datasummary/routes/'>{gettext('Routes')}</MenuItemAnchor>
            <MenuItemAnchor href='/datasummary/areas/'>{gettext('Areas')}</MenuItemAnchor>
            <MenuItemAnchor href='/datasummary/waypoints/'>{gettext('Waypoints')}</MenuItemAnchor>
            <MenuItemAnchor href='/datasummary/photos/'>{gettext('Photos')}</MenuItemAnchor>
            <MenuItemAnchor href='/datasummary/saved/'>{gettext('Hikes')}</MenuItemAnchor>
            <MenuItemAnchor href='/datasummary/maps/'>{gettext('Maps')}</MenuItemAnchor>
            <MenuItemAnchor href='/datasummary/mapsources/'>
                {gettext('Map Sources')}
            </MenuItemAnchor>
            <MenuItemAnchor href='/datasummary/folders/'>{gettext('Folders')}</MenuItemAnchor>

            <Divider style={dividerStyle} />

            <MenuItemAnchor href='/map/?sidebar=import'>{gettext('Upload')}</MenuItemAnchor>

            <Divider style={dividerStyle} />

            {isTeamAdmin && <MenuItemAnchor href='/teams/'>My Teams</MenuItemAnchor>}

            <MenuItemAnchor href='/team/'>Create a Team</MenuItemAnchor>

            <Divider style={dividerStyle} />

            <MenuItemAnchor href='/profile/?utm_source=header&utm_campaign=profile'>
                {gettext('My Profile')}
            </MenuItemAnchor>
            <MenuItemAnchor href='/user/subscriptions/'>
                {gettext('My Subscriptions')}
            </MenuItemAnchor>
            <MenuItemAnchor href='/settings/'>{gettext('Settings')}</MenuItemAnchor>
            <MenuItemAnchor href='/logout/'>{gettext('Log out')}</MenuItemAnchor>

            {isStaff && <AdminNavigation />}
        </MenuList>
    );
});

export const HeaderUser = {Toolbar, HamburgerMenu};
